'use client';

import {
  Children,
  cloneElement,
  isValidElement,
  memo,
  type MouseEvent,
  type PropsWithChildren,
  useLayoutEffect,
} from 'react';
import Tea from '@dp/byted-tea-sdk-oversea';
import { useMountEffect } from '@react-hookz/web';
import { SWRConfig } from 'swr';

import 'client-only';

import {
  CLIENT_EVENT_PARAM_KEY,
  type EventParams,
  sendPredefinedEvent,
  type TriggerParams,
} from './utils';

export function TeaInitializer({ userId }: { userId?: string }) {
  useMountEffect(() => {
    Tea.init({
      app_id: 579340,
      channel_type: 'tcpy',
      channel: 'sg',
      ab_channel_domain: 'https://libraweb-sg.tiktok.com',
      enable_stay_duration: true,
    });
    if (userId) {
      Tea.config({
        user_unique_id: userId,
        _staging_flag: process.env.BUILD_TYPE === 'online' ? 0 : 1,
      });
    } else {
      Tea.config({
        _staging_flag: process.env.BUILD_TYPE === 'online' ? 0 : 1,
      });
    }
    Tea.start();
  });
  return null;
}

export const ExposureEvent = memo(function ExposureEvent(props: TriggerParams) {
  useMountEffect(() => {
    sendPredefinedEvent(props);
  });
  return null;
});

export const ClickEvent = memo(function ClickEvent({
  children,
  ...otherProps
}: TriggerParams & PropsWithChildren) {
  const child = Children.only(children);
  if (!isValidElement(child) || child.type === (<></>).type) {
    throw new Error(
      `failed to attach event listener to ${isValidElement(child) ? child.type : child}`
    );
  }

  return cloneElement<any>(child, {
    onClickCapture: (e: MouseEvent) => {
      if (child.props.onClickCapture) {
        child.props.onClickCapture(e);
      }

      sendPredefinedEvent(otherProps);
    },
  });
});

type DataToBeHydrated = Partial<{
  [key in EventParams]: string;
}>;
/**
 * retrieve data from server side and hydrate at client side
 */
export function Hydrate({ data }: { data: DataToBeHydrated }) {
  useLayoutEffect(() => {
    const localData = SWRConfig.defaultValue.cache.get(CLIENT_EVENT_PARAM_KEY)?.data || {};

    SWRConfig.defaultValue.cache.set(CLIENT_EVENT_PARAM_KEY, {
      data: {
        ...localData,
        ...data,
      },
    });
  }, [data]);
  return null;
}
